import React, { useState } from "react";
import { ClearUpdateEntraIdUserTracking, UpdateEntraIdUserTracking } from "../../../../store/TrackingOperationAction";
import { CoreTracking } from "../../../../models/CoreTracking";
import { CoreEntraIdUserTrackingConflict, CoreEntraIdUserTrackingUpdate } from "../../../../models/CoreTrackingOperation";
import { useDispatch, useSelector } from "react-redux";
import { RequestState } from "../../../../models/RequestState";
import ModalComponent from "../../../misc/ModalComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, FormFeedback, FormGroup, Input, Label } from "reactstrap";
import StatusCode from "../../../../util/StatusCode";
import EntraIdUserTrackingOperationsValidationResult from "./EntraIdUserTrackingOperationsValidationResult";
import CoreApiError from "../../../misc/CoreApiError";
import Loading from "../../../misc/Loading";
import { EntraIdUserTrackingMetadata } from "../../../../models/TrackingMetadata";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";



interface EditEntraIdUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const EditEntraIdUserTrackingWizard: React.FC<EditEntraIdUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const metadata = JSON.parse(props.tracking.metadata) as EntraIdUserTrackingMetadata;
    const [wizardStep, setWizardStep] = useState<'input' | 'verify' | 'done'>('input');
    const [update, setUpdate] = useState<CoreEntraIdUserTrackingUpdate>({ primarySmtpAddress: metadata.PrimaryEmail, userPrincipalName: metadata.UserPrincipalName.Value });
    const updateEntraIdUserTracking = useSelector<RootState, RequestState<CoreEntraIdUserTrackingConflict[]>>(state => state.trackingOperation.updateEntraIdUserTracking)
    const initial: CoreEntraIdUserTrackingUpdate = { primarySmtpAddress: metadata.PrimaryEmail, userPrincipalName: metadata.UserPrincipalName.Value };
    const { t } = useTranslation(['schoolAdministration', 'common']);
    const updateUserPrincipalName: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            userPrincipalName: e.target.value,
        });
    }

    const updatePrimarySmtpAddress: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            primarySmtpAddress: e.target.value,
        });
    }

    const submitInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        if (update.primarySmtpAddress && update.userPrincipalName) {
            UpdateEntraIdUserTracking(update, props.tracking.trackingId, true)(dispatch);
            setWizardStep('verify');
        }
    }

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        if (update.primarySmtpAddress && update.userPrincipalName) {
            UpdateEntraIdUserTracking(update, props.tracking.trackingId, false)(dispatch);
            setWizardStep('done');
        }
    }

    const close = (success: boolean) => {
        ClearUpdateEntraIdUserTracking()(dispatch);
        setWizardStep('input');
        setUpdate({ primarySmtpAddress: '', userPrincipalName: '' });
        props.close(success);
    }

    switch (wizardStep) {
        case 'input': {
            if (isCurrentlyLifecycleManaged(props.tracking)) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.lifeCycleError', {ns: 'schoolAdministration'})}</ModalComponent>;
            }

            return <ModalComponent
                isOpen={true}
                toggleModal={() => close(false)}
                header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                update={submitInput}
                disabled={!isValid(update) || !hasChanges(update, initial)}
                confirm={t('action.validate', {ns: 'common'})}
                cancel={t('action.cancel', {ns: 'common'})}>
                <Form>
                    <FormGroup key="entraIdUserPrincipalName">
                        <Label for="entraIdUserPrincipalName">{t('trackingOperations.entraId.edit.upnLabel', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="entraIdUserPrincipalName"
                            id="entraIdUserPrincipalName"
                            placeholder={t('trackingOperations.general.emailPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.userPrincipalName === ''}
                            value={update.userPrincipalName}
                            onChange={updateUserPrincipalName} />
                        <FormFeedback>{t('trackingOperations.entraId.edit.upnFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                    <FormGroup key="entraIdPrimarySmtpAddress">
                        <Label for="entraIdPrimarySmtpAddress">{t('trackingOperations.entraId.edit.smtpLabel', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="entraIdPrimarySmtpAddress"
                            id="entraIdPrimarySmtpAddress"
                            placeholder={t('trackingOperations.general.emailPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.primarySmtpAddress === ''}
                            value={update.primarySmtpAddress}
                            onChange={updatePrimarySmtpAddress} />
                        <FormFeedback>{t('trackingOperations.entraId.edit.smtpFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalComponent>;
        }
        case 'verify': {
            if (updateEntraIdUserTracking.code === StatusCode.COMPLETE) {
                if (!hasChanges(update, initial)) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                        cancel={t('action.cancel', {ns: 'common'})}>{t('trackingOperations.general.verifyNoChanges', {ns: 'schoolAdministration'})}</ModalComponent>
                } else if (updateEntraIdUserTracking.data.length === 0) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                        update={confirmInput}
                        confirm={t('action.confirm', {ns: 'common'})}
                        cancel={t('action.cancel', {ns: 'common'})}>
                        <p><FontAwesomeIcon title={t('status.OK', {ns: 'common'})} color="green" icon={['fas', 'check-circle']} />{t('trackingOperations.general.noErrorsConfirm', {ns: 'schoolAdministration'})}</p>
                        {(update.userPrincipalName !== initial.userPrincipalName) && <>
                            <div className="mt-3">
                                <b>{t('trackingOperations.entraId.edit.changeUsername', {ns: 'schoolAdministration'})}</b>
                                <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                    <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                    <div>{initial.userPrincipalName}</div>
                                    <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                    <div>{update.userPrincipalName}</div>
                                </div>
                            </div>
                        </>}
                        {(update.primarySmtpAddress !== initial.primarySmtpAddress) && <>
                            <div className="mt-3">
                                <b>{t('trackingOperations.entraId.edit.changeSmtp', {ns: 'schoolAdministration'})}</b>
                                <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                    <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                    <div>{initial.primarySmtpAddress}</div>
                                    <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                    <div>{update.primarySmtpAddress}</div>
                                </div>
                            </div>
                        </>}
                    </ModalComponent>
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <p><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.general.conflictsDiscovered', {ns: 'schoolAdministration'})}</p>
                    <EntraIdUserTrackingOperationsValidationResult primarySmtpAddress={update.primarySmtpAddress} userPrincipalName={update.userPrincipalName} conflicts={updateEntraIdUserTracking.data} />
                </ModalComponent>;
            } else if (updateEntraIdUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={updateEntraIdUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (updateEntraIdUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>{t('trackingOperations.general.changeCompleted', {ns: 'schoolAdministration'})}</ModalComponent>
            } else if (updateEntraIdUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={updateEntraIdUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

const hasChanges = (update: CoreEntraIdUserTrackingUpdate, initial: CoreEntraIdUserTrackingUpdate) => {
    if (update.primarySmtpAddress !== initial.primarySmtpAddress) {
        return true;
    }

    if (update.userPrincipalName !== initial.userPrincipalName) {
        return true;
    }

    return false;
}

const isValid = (update: CoreEntraIdUserTrackingUpdate) => update.primarySmtpAddress !== '' && update.userPrincipalName !== '';

const isCurrentlyLifecycleManaged = (trackig: CoreTracking) => trackig.active && !trackig.deleted && (trackig.inactivated || trackig.archived);

export default EditEntraIdUserTrackingWizard;
